import React, { useEffect, useState } from "react";
import "../../assets/css/Services.css";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

// meta data
import { Helmet } from "react-helmet";
import data from "../../component/ApiUrl/meta_api";

/* ─── Data ─────────────────────────────────────────────────────────────────── */
const cards = [
  {
    icon: "fa-magnifying-glass-chart",
    title: "SEO & Content Marketing",
    para: "Boost your online visibility and drive organic traffic with our data-driven SEO strategies and compelling content crafted to rank higher and engage your target audience effectively.",
    list: [
      "On-Page & Off-Page SEO",
      "Keyword Research & Strategy",
      "Blog, Article & Copywriting",
      "Technical SEO & Link Building",
    ],
  },

  {
    icon: "fa-hashtag",
    title: "Social Media Marketing",
    para: "Build a powerful brand presence across all major social platforms with our strategic social media management from content creation to community engagement and growth campaigns.",
    list: [
      "Social Media Strategy & Planning",
      "Instagram, Facebook & LinkedIn",
      "Content Creation & Scheduling",
      "Influencer & Community Management",
    ],
  },

  {
    icon: "fa-bullhorn",
    title: "Paid Ads & PPC Campaigns",
    para: "Maximize your return on investment with our performance-driven paid advertising campaigns precisely targeted to reach the right audience at the right time across all digital platforms.",
    list: [
      "Google Ads & Search Campaigns",
      "Meta & Instagram Ads",
      "Retargeting & Remarketing",
      "Budget & Bid Optimization",
    ],
  },

  {
    icon: "fa-photo-film",
    title: "Branding, Design & Video Production",
    para: "Create a lasting brand impression with our creative design and video production services from powerful brand identities to engaging reels, ads, and corporate videos that tell your story.",
    list: [
      "Brand Identity & Logo Design",
      "Reels, Videos & Motion Graphics",
      "Marketing Collateral Design",
      "Brand Strategy & Storytelling",
    ],
  },

  {
    icon: "fa-code",
    title: "Website Design & Development",
    para: "Launch a professional, high-converting website that represents your brand with impact built with modern design, fast performance, and optimized for both search engines and users.",
    list: [
      "Business & E-Commerce Websites",
      "Landing Page Development",
      "UI/UX Design & Prototyping",
      "Website Speed & SEO Optimization",
    ],
  },

  {
    icon: "fa-chart-line",
    title: "Analytics, Email & Performance Reporting",
    para: "Make smarter marketing decisions with our data-driven analytics, email automation, and detailed performance reporting turning numbers into clear strategies for continuous growth.",
    list: [
      "Google Analytics & Tag Setup",
      "Email Campaigns & Automation",
      "Conversion Rate Optimization",
      "Monthly ROI & Campaign Reports",
    ],
  },
];

const STEPS = [
  {
    icon: "fa-magnifying-glass",
    title: "Market & Website Audit",
    desc: "Analyze website, competitors, audience, and identify SEO growth opportunities.",
  },
  {
    icon: "fa-lightbulb",
    title: "Strategy & Campaign Planning",
    desc: "Create data-driven marketing strategy aligned with business goals.",
  },
  {
    icon: "fa-gears",
    title: "Implementation & Optimization",
    desc: "Execute campaigns, improve SEO, ads, and optimize marketing performance.",
  },
  {
    icon: "fa-chart-line",
    title: "Performance Tracking & Analytics",
    desc: "Track traffic, rankings, conversions, and campaign performance with analytics.",
  },
  {
    icon: "fa-rocket",
    title: "Growth & Continuous Improvement",
    desc: "Scale campaigns, refine strategy, and drive long-term digital growth.",
  },
];

const FAQ_DATA = [
  [
    "What Digital Marketing services do you offer?",
    "We provide complete Digital Marketing Services including SEO (Search Engine Optimization), Google Ads (PPC), Social Media Marketing, Content Marketing, Lead Generation, and Marketing Automation to grow your business online.",
  ],
  [
    "How can Digital Marketing help my business grow?",
    "Digital Marketing helps increase your online visibility, drive targeted traffic, generate quality leads, and improve conversions using SEO, paid ads, and social media strategies.",
  ],
  [
    "Do you provide SEO services for websites?",
    "Yes, we offer professional SEO services including keyword research, on-page SEO, technical SEO, and link building to improve your Google rankings and organic traffic.",
  ],
  [
    "What is included in your Google Ads service?",
    "Our Google Ads (PPC) service includes campaign setup, keyword targeting, ad copy creation, A/B testing, and continuous optimization to maximize ROI and reduce cost per lead.",
  ],
  [
    "Do you manage social media marketing?",
    "Yes, we handle complete Social Media Marketing including content creation, post scheduling, ad campaigns, audience targeting, and engagement strategies for platforms like Instagram, Facebook, and LinkedIn.",
  ],
  [
    "How long does it take to see results from Digital Marketing?",
    "SEO typically takes 3–6 months for visible results, while Google Ads and Social Media Ads can generate leads within days. We focus on both short-term and long-term growth strategies.",
  ],
  [
    "Do you provide lead generation services?",
    "Yes, we specialize in lead generation using SEO, Google Ads, landing pages, and funnel optimization to bring high-quality leads for your business.",
  ],
  [
    "How much do Digital Marketing services cost?",
    "The cost depends on your business goals, competition, and services required. We offer flexible and affordable Digital Marketing packages tailored to your needs.",
  ],
  [
    "Will I get reports and performance tracking?",
    "Yes, we provide regular reports including website traffic, keyword rankings, ad performance, and lead conversions with detailed analytics.",
  ],
  [
    "How can I get started with your Digital Marketing services?",
    "You can contact us for a free consultation. We will analyze your business, understand your goals, and provide a customized Digital Marketing strategy and proposal.",
  ],
];

/* ─── Small components ──────────────────────────────────────────────────────── */
function SectionLabel({ text }) {
  return (
    <p
      className="fw-semibold text-uppercase mb-1"
      style={{ fontSize: 20, color: "#cc0000", letterSpacing: 1 }}
    >
      {text}
    </p>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className={`rs-faq-item rs-anim${open ? " open" : ""}`}
      style={{ transitionDelay: `${idx * 55}ms` }}
    >
      <button className="rs-faq-btn" onClick={() => setOpen((o) => !o)}>
        <span className="rs-faq-q">{q}</span>
        <span className="rs-faq-icon">
          <i className="fa-solid fa-plus" />
        </span>
      </button>
      <div className="rs-faq-body">{a}</div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function DigitalMarketing() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);

  // scroll top
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const fetchData = () => {
    const DigitalMarketingMeta = data[0]?.digitalmarketing_page;
    if (DigitalMarketingMeta) {
      setMeta(DigitalMarketingMeta);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: "ease-in-out",
    });

    if (!document.getElementById("rs-styles")) {
      const s = document.createElement("style");
      s.id = "rs-styles";
      document.head.appendChild(s);
    }
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const fa = document.createElement("link");
      fa.rel = "stylesheet";
      fa.href =
        "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css";
      document.head.appendChild(fa);
    }
    if (!document.querySelector('link[href*="Poppins"]')) {
      const gf = document.createElement("link");
      gf.rel = "stylesheet";
      gf.href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap";
      document.head.appendChild(gf);
    }
  }, []);

  return (
    <div className="body">
      <Helmet>
        <title>{meta ? meta.title : "Digital Marketing"}</title>
      </Helmet>

      {/* ═════════
          1. HERO 
      ═════════════ */}
      <div
        className="backgroundCommenImage position-relative d-flex align-items-center mb-5"
        style={{
          backgroundImage: `url(${require("../../assets/img/Service/digital marketing/banner.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "60vh",
        }}
      >
        {/* Overlay */}
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ background: "rgba(0, 0, 0, 0.8)", zIndex: 1 }}
        ></div>

        {/* Content */}
        <div
          className="container position-relative text-center text-white py-4 py-md-5"
          style={{ zIndex: 2 }}
        >
          {/* Badge */}
          <span
            className="fw-semibold d-inline-block mb-3 px-3 py-1 rounded-pill"
            style={{
              background: "#cc0000",
              fontSize: "clamp(10px,3vw,12px)",
              letterSpacing: "0.5px",
            }}
            data-aos="fade-down"
            data-aos-delay="100"
          >
            ✦ AI-Powered · Results-Driven · {new Date().getFullYear()} Certified
          </span>

          {/* Heading */}
          <h1
            className="fw-semibold mb-3"
            style={{
              fontSize: "clamp(18px,5vw,28px)",
              lineHeight: 1.4,
            }}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Digital Marketing
            <br />
            <span style={{ color: "#cc0000" }}>
              SEO · Ads · AI · Social · Automation
            </span>
          </h1>

          {/* Text */}
          <p
            className="mx-auto mb-4 px-2 px-md-0"
            style={{
              fontSize: "clamp(13px,3.5vw,15px)",
              maxWidth: "650px",
              lineHeight: 1.7,
              color: "#ebe6e6",
            }}
            data-aos="fade-up"
            data-aos-delay="350"
          >
            Skimitar Hindusthan provides hands-on Digital Marketing training
            with real campaigns, real data, and practical strategies for real
            business growth.
          </p>
        </div>
      </div>

      {/* ════════════
          2. INTRO
      ═════════════════ */}
      <div className="container margin_top container_width">
        <div className="row align-items-center justify-content-center g-5">
          {/* Left */}
          <div className="col-lg-6" data-aos="fade-right" data-aos-delay="100">
            <SectionLabel text="Digital Marketing Services" />

            <h2 className="fw-semibold mb-3" style={{ fontSize: 20 }}>
              About Our Digital Marketing Services
            </h2>

            <p className="mb-3 content-text">
              <span className="fw-semibold">Skimitar Hindustan Pvt Ltd</span>{" "}
              delivers results-driven Digital Marketing solutions using SEO,
              paid advertising, AI, social media marketing, and automation. Our
              data-driven strategies help businesses increase online visibility,
              attract the right audience, and generate consistent leads.
            </p>

            <ul className="list-unstyled content-list mt-3">
              {[
                "Increase website traffic and search engine visibility",
                "Generate high-quality leads and improve conversion rates",
                "Reduce marketing costs with targeted campaigns",
                "Strengthen brand authority across digital platforms",
                "Utilize AI-driven insights for smarter marketing decisions",
                "Automate marketing workflows for faster business growth",
              ].map((item, i) => (
                <li
                  key={i}
                  className="d-flex align-items-start gap-2 mb-2 small"
                  data-aos="fade-up"
                  data-aos-delay={`${100 + i * 80}`}
                >
                  <i className="fa-solid fa-check text-danger mt-1"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right */}
          <div
            className="col-lg-6 text-center"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            <div className="position-relative d-inline-block w-100">
              {/* Image */}
              <img
                className="img-fluid rounded-4 "
                src={require("../../assets/img/Service/digital marketing/img-1.jpg")}
                alt="DM Image-1"
              />

              {/* Stat */}
              <div className="retro-stat">
                <i class="fa-solid fa-bullhorn text-red fs-5"></i>
                <div>
                  <div className="fw-bold fs-5">350+</div>
                  <div className="small text-muted">Digital Marketing</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          3. SERVICE DETAILS tabs + feature list
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top pb-5">
        <div className="container container_width">
          <div className="mb-1" data-aos="fade-up">
            <SectionLabel text="Our Digital Marketing Services" />
          </div>

          <div className="row mb-4">
            <div className="col-lg-7" data-aos="fade-up" data-aos-delay="100">
              <h2 className="fw-semibold mb-2" style={{ fontSize: 20 }}>
                Comprehensive Digital Marketing Solutions
              </h2>

              <p className="mb-3 content-text">
                From improving search engine rankings to managing
                high-performance advertising campaigns, we handle every aspect
                of modern digital marketing to ensure your brand reaches the
                right audience at the right time.
              </p>
            </div>

            <div
              className="col-lg-5 text-lg-end mt-3 mt-lg-0"
              data-aos="fade-right"
              data-aos-delay="100"
            >
              <a href="#contact" className="btn btn-red px-4 py-2">
                Discuss Your Project
                <i className="fa-solid fa-arrow-right ms-2"></i>
              </a>
            </div>
          </div>

          {/* Content */}
          <div className="row g-3">
            {cards.map((s, i) => (
              <div
                key={i}
                className="col-12 col-sm-6 col-lg-4"
                data-aos="fade-up"
                data-aos-delay={`${i * 100}`}
              >
                <div className="card h-100 border-0 shadow service-card">
                  <div className="card-body position-relative p-4">
                    {/* Number */}
                    {/* <span className="service-number">
                      {String(i + 1).padStart(2, "0")}
                    </span> */}

                    {/* Icon */}
                    <div className="service-icon mb-3">
                      <i className={`fa-solid ${s.icon}`}></i>
                    </div>

                    {/* title */}
                    <h5
                      className="fw-bold text-red mb-2"
                      style={{ fontSize: 15 }}
                    >
                      {s.title}
                    </h5>

                    {/* para */}
                    <p className="small text-muted mb-2">{s.para}</p>

                    {/* list */}
                    <ul className="list-unstyled">
                      {s.list.map((item, i) => (
                        <li
                          key={i}
                          className="d-flex align-items-start gap-2 mb-2 small"
                          data-aos="fade-up"
                          data-aos-delay={`${100 + i * 80}`}
                        >
                          <i className="fa-solid fa-check text-danger mt-1"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <button className="btn btn-red btn-sm mt-2">
                      Enquire Now
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          4. HOW IT WORKS 5-step process
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top py-5 bg-light" id="process">
        <div className="container container_width">
          <div className="mb-2" data-aos="fade-up">
            <SectionLabel text="How Our Digital Marketing Strategy Works" />
          </div>

          <div className="mb-5" data-aos="fade-up" data-aos-delay="100">
            <h2 className="fw-semibold mb-3" style={{ fontSize: 20 }}>
              Our 5-Step Digital Marketing Process
            </h2>
            <p className="mb-3 content-text">
              Our approach focuses on research, strategy, implementation, and
              optimization to improve marketing performance. We help businesses
              increase visibility, attract the right audience, and generate
              quality leads.
            </p>
          </div>

          <div className="row g-4 text-center justify-content-center">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className="col-12 col-md-4 col-lg position-relative"
                data-aos="fade-up"
                data-aos-delay={`${i * 120}`}
              >
                {/* connector */}
                {i < STEPS.length - 1 && (
                  <div className="process-line d-none d-lg-block"></div>
                )}

                {/* circle */}
                <div className="process-circle mx-auto mb-3 d-flex align-items-center justify-content-center position-relative">
                  <i className={`fa-solid ${step.icon} text-red fs-5`}></i>

                  <span className="badge bg-red position-absolute process-number">
                    {i + 1}
                  </span>
                </div>

                <h5 className="fw-bold small mb-1">{step.title}</h5>

                <p className="text-muted small mb-0">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          5. FAQ
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top pb-5 rs-bg-alt" id="faq">
        <div className="container container_width py-5">
          <div className="text-center rs-anim mb-1" data-aos="fade-up">
            <SectionLabel text="FAQ" />
          </div>
          <div
            className="text-center rs-anim mb-5"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="fw-semibold" style={{ fontSize: 20 }}>
              Frequently Asked Questions
            </h2>
            <p className="mb-3" style={{ fontSize: 15 }}>
              Everything you need to know about our Digital Marketing Services
              including SEO, Google Ads, Social Media Marketing, Lead
              Generation, and ROI-driven strategies.
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-9">
              {FAQ_DATA.map(([q, a], i) => (
                <FAQItem key={i} q={q} a={a} idx={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          6. GET IN TOUCH CTA
      ══════════════════════════════════════════════════════════════ */}
      <section id="contact">
        <div className="p-5" style={{ background: "#cc0000" }}>
          <div
            className="d-flex align-items-center justify-content-center gap-3 mb-3"
            data-aos="fade-down"
          >
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
            <span
              className="text-uppercase fw-semibold text-white text-opacity-75"
              style={{
                letterSpacing: "2px",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }}
            >
              Grow Your Business Online
            </span>
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
          </div>

          <div className="row g-4 align-items-center">
            <div
              className="col-lg-7 text-center text-lg-start"
              data-aos="fade-right"
              data-aos-delay="100"
            >
              <h2 className="fw-bolder text-white text-uppercase mb-3">
                Boost Your Business with <br />
                Digital Marketing Experts
              </h2>

              <p
                className="text-white text-opacity-75 mb-0"
                style={{ lineHeight: "1.75" }}
              >
                Drive more traffic, generate high-quality leads, and increase
                your sales with our expert Digital Marketing services. From SEO
                and Google Ads to Social Media Marketing and Performance
                Marketing, we help your business grow faster with data-driven
                strategies and measurable results.
              </p>
            </div>

            <div
              className="col-lg-5"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-end">
                <button
                  className="btn oc-btn-white px-4 py-3 fw-bold text-uppercase"
                  onClick={() => navigate("/contact")}
                >
                  Get Free Consultation →
                </button>

                <a
                  className="btn oc-btn-ghost px-4 py-3 fw-bold text-uppercase"
                  href="tel:8428010909"
                >
                  📞 (+91) 8428010909
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
